import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { LOGIN_ROUTE, LOGOUT_ROUTE, REGISTER_ROUTE } from '@immediate_media/auth-constants';
import { useCurrentUser, useLoggedIn } from '@immediate_media/auth-lib-react';
import { ACCOUNT_SETTINGS_PATHS } from './constants';
import useUserProfile from './use-user-profile';
import useSettings from './use-settings';
const useAccountSettings = redirect => {
  const [accountSettings, setAccountSettings] = useState(null);
  const [currentUser, isLoadingCurrentUser] = useCurrentUser();
  const isLoggedIn = useLoggedIn();
  const settings = useSettings();
  const {
    t
  } = useTranslation();
  const profileDetails = useUserProfile();
  const {
    enabled: accountSettingsEnabled,
    subscriptionsEnabled
  } = settings?.accountSettings || {};
  const accountSettingsLabel = accountSettingsEnabled ? t('pocketHooks.useAccountSettings.accountMenu.accountSettings') : 'Settings'; // No translation, as this will be removed soon
  const accountSettingsHref = accountSettingsEnabled ? ACCOUNT_SETTINGS_PATHS.myDetails : '/user-settings'; // No constant, as this will be removed soon

  const getAccountMenu = useCallback(() => {
    if (isLoadingCurrentUser) {
      return [];
    }
    if (!isLoggedIn && !currentUser) {
      return [];
    }
    const menu = [{
      label: accountSettingsLabel,
      relativeUrl: accountSettingsHref
    }, {
      label: t('pocketHooks.useAccountSettings.accountMenu.signOut'),
      relativeUrl: LOGOUT_ROUTE
    }];

    // Add child menu items for FAB 2 account settings
    if (accountSettingsEnabled) {
      menu[0].children = [{
        label: t('pocketHooks.useAccountSettings.accountMenu.myDetails'),
        relativeUrl: ACCOUNT_SETTINGS_PATHS.myDetails
      }, {
        label: t('pocketHooks.useAccountSettings.accountMenu.contactPreferences'),
        relativeUrl: ACCOUNT_SETTINGS_PATHS.contactPreferences
      }];
      if (subscriptionsEnabled) {
        menu[0].children.push({
          label: t('pocketHooks.useAccountSettings.accountMenu.mySubscriptions'),
          relativeUrl: ACCOUNT_SETTINGS_PATHS.mySubscriptions
        });
      }
    }

    // Edge case, should never happen, but if the OIDC FES is down we still want to show
    // the user the option to sign out/access their settings (which will redirect to the login page)
    if (isLoggedIn && !currentUser) {
      return menu;
    }
    if (currentUser.vanityId && settings.hub && settings.hub.enabled) {
      if (settings.hub.ugc.enabled) {
        menu.unshift({
          label: settings.hub.ugc.label,
          relativeUrl: `/user/${currentUser.vanityId}/${settings.hub.ugc.href}`
        });
      }
      if (settings.hub.items) {
        [...settings.hub.items].reverse().forEach(item => {
          menu.unshift({
            label: item.pluralLabel,
            relativeUrl: `/user/${currentUser.vanityId}/${item.href}`
          });
        });
      }
      menu.unshift({
        label: t('pocketHooks.useAccountSettings.accountMenu.overview'),
        relativeUrl: `/user/${currentUser.vanityId}`
      });
    }
    return menu;
  }, [currentUser, isLoadingCurrentUser, isLoggedIn, accountSettingsLabel, accountSettingsHref, accountSettingsEnabled, subscriptionsEnabled, settings.hub, t]);
  useEffect(() => {
    const query = redirect ? encodeURIComponent(`?redirect=${redirect}`) : '';
    setAccountSettings({
      profileDetails,
      accountNavOptions: {
        label: settings.hub?.enabled && settings.hub?.header ? settings.hub.header : t('pocketHooks.useAccountSettings.accountNavOptions.label'),
        menuItems: getAccountMenu(),
        signInUrl: `${LOGIN_ROUTE}${query}`,
        registerUrl: `${REGISTER_ROUTE}${query}`,
        settingsUrl: `${accountSettingsHref}${query}`
      }
    });
  }, [getAccountMenu, accountSettingsHref, profileDetails, redirect, settings, settings.hub?.enabled, settings.hub?.header, t]);
  return accountSettings;
};
export default useAccountSettings;